export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

const BASE = '/api/v1'

/**
 * Fired when the session is over for good - the refresh token is expired or
 * revoked. The auth context listens for it and returns the app to sign-in.
 */
export const SESSION_EXPIRED_EVENT = 'ix:session-expired'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[2]) : null
}

type Options = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null>
}

// A 401 from these means "those credentials are wrong", not "your session
// expired", so they are never answered with a silent refresh.
const NO_REFRESH = [
  /^\/auth\/(login|signup|refresh|logout|mfa)(\/|$|\?)/,
  /^\/auth\/passkey\//,
  /^\/auth\/sso\//,
  /^\/invitations\//,
]

type LockManagerLike = { request: <T>(name: string, callback: () => Promise<T>) => Promise<T> }

let renewing: Promise<boolean> | null = null

/**
 * Renew the session with the refresh cookie. The access token lives 15
 * minutes, so a page left open - or one that polls, like Analytics - reaches
 * this routinely.
 *
 * Refresh tokens rotate and a reused one revokes the whole session, so two
 * renewals must never race: requests in this tab share one renewal, and tabs
 * take turns through a Web Lock (the second tab then renews with the cookie
 * the first one just received).
 */
function renewSession(): Promise<boolean> {
  if (renewing) return renewing
  const run = async (): Promise<boolean> => {
    // /auth/refresh is a state-changing POST, so it carries the double-submit
    // CSRF token like every other write - this raw fetch bypasses api(), so the
    // header is set here explicitly.
    const csrf = readCookie('__Host-ix_csrf') ?? readCookie('__Secure-ix_csrf') ?? readCookie('ix_csrf')
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
    })
    return res.ok
  }
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks
  const attempt: Promise<boolean> = locks ? locks.request<boolean>('insuronix-session-renewal', run) : run()
  const current = attempt
    .catch(() => false)
    .finally(() => {
      renewing = null
    })
  renewing = current
  return current
}

export async function api<T = unknown>(path: string, opts: Options = {}, retried = false): Promise<T> {
  const { method = 'GET', body, signal, query } = opts

  let url = `${BASE}${path}`
  if (query) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    }
    const s = qs.toString()
    if (s) url += `?${s}`
  }

  // FormData must be sent untouched: the browser sets multipart/form-data with
  // the boundary, and JSON.stringify would turn a file upload into "{}".
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData

  const headers: Record<string, string> = {}
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'

  // Double-submit CSRF: echo the readable cookie back in a header. Read on every
  // attempt - a renewal issues a new token, and a retry must send that one.
  if (method !== 'GET' && method !== 'HEAD') {
    // Over HTTPS the API prefixes the name (__Host-ix_csrf) so the browser
    // binds it to this exact origin; the plain name is the COOKIE_SECURE=false case.
    const csrf = readCookie('__Host-ix_csrf') ?? readCookie('__Secure-ix_csrf') ?? readCookie('ix_csrf')
    if (csrf) headers['X-CSRF-Token'] = csrf
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    signal,
  })

  if (res.status === 401 && !retried && !NO_REFRESH.some((re) => re.test(path))) {
    if (await renewSession()) return api<T>(path, opts, true)
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const parsed = text ? safeJson(text) : null

  if (!res.ok) {
    const detail = parsed && typeof parsed === 'object' && 'detail' in parsed ? (parsed as { detail: unknown }).detail : null
    // A 422 lists each problem; show their messages rather than "[object Object]".
    const message =
      (Array.isArray(detail)
        ? detail
            .map((d) => String((d as { msg?: unknown })?.msg ?? '').replace(/^Value error, /, ''))
            .filter(Boolean)
            .join(' ')
        : detail !== null
          ? String(detail)
          : null) || res.statusText
    throw new ApiError(res.status, message, parsed)
  }

  return parsed as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const get = <T,>(path: string, query?: Options['query']) => api<T>(path, { query })
export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body })
export const patch = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body })
export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body })
export const del = <T,>(path: string) => api<T>(path, { method: 'DELETE' })
